import { useLazyQuery, useMutation } from '@apollo/client';
import { Grid, makeStyles } from '@material-ui/core';
import { DELETE_MEDIA } from 'apollo/mutations/Gallery';
import { GET_GALLERY_STRUCTURE } from 'apollo/queries/Gallery';
import GalleryImage from 'components/atoms/buttons/gallery/GalleryImage';
import WarningDialog from 'components/atoms/buttons/popup/WarningDialog';
import { GalleryContext } from 'context/gallery/GalleryContext';
import React, { useContext, useEffect, useState } from 'react';
import { IFsNode } from 'ts/interfaces';

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        marginTop: 20,
        justifyContent: 'flex-start',
        overflow: 'scroll',
    },
    succesMessage: {
        textAlign: 'center',
        fontWeight: 'bold',
    },
}));

const ImagesBlock = ({ handleFileSelection }: { handleFileSelection?: (file: string) => void }) => {
    const classes = useStyles();
    const [data, setData] = useState<IFsNode>();
    const [files, setFiles] = useState<IFsNode[]>();
    const [selectedFile, setSelectedFile] = useState<{ id: string; file: string }>({ id: '', file: '' });
    const [showWarning, setShowWarning] = useState(false);
    const [removableFile, setRemovableFile] = useState<{ from: string; remove: string }>();
    const [getFolderImages] = useLazyQuery(GET_GALLERY_STRUCTURE, {
        fetchPolicy: 'network-only',
        onCompleted: ({ galleryStructure }) => {
            return setData(JSON.parse(galleryStructure));
        },
    });

    const { state: galleryState, setGalleryState } = useContext(GalleryContext);

    const [deleteMedia] = useMutation(DELETE_MEDIA, {
        onCompleted: () => {
            getFolderImages({ variables: { from: galleryState.breadcrumbs } });
        },
    });

    const handleDelete = (from: string, remove: string) => {
        setShowWarning(true);
        setRemovableFile({ from, remove });
    };

    const handleCloseWarning = () => {
        setShowWarning(false);
    };

    const handleContinueWarning = () => {
        setShowWarning(false);
        deleteMedia({ variables: removableFile });
    };

    useEffect(() => {
        if (galleryState.breadcrumbs) {
            const getImages = async () => {
                await getFolderImages({ variables: { from: galleryState.breadcrumbs } });
            };

            getImages();
        }
    }, [galleryState.breadcrumbs]);

    useEffect(() => {
        if (galleryState.hasUploaded) {
            const getImages = async () => {
                await getFolderImages({ variables: { from: galleryState.breadcrumbs } });
            };

            getImages();
            setGalleryState({ ...galleryState, hasUploaded: false });
        }
    }, [galleryState.hasUploaded]);

    useEffect(() => {
        if (data) {
            setFiles(data?.children?.filter((item: IFsNode) => item.file));
        }
    }, [data]);

    return (
        <Grid
            container
            className={classes.root}
            justify='flex-start'
            alignItems='flex-start'
            spacing={2}
            style={{ height: galleryState.isUploading ? 'calc(100vh - 500px)' : 'calc(100vh - 175px)' }}
        >
            {files && files.length > 0 ? (
                files?.map((file: Pick<IFsNode, 'file' | 'id'>) => {
                    if (file.file) {
                        return (
                            <GalleryImage
                                galleryImage={file?.file}
                                key={file.id || file.file}
                                handleDelete={handleDelete}
                                onClick={() => {
                                    file?.file && handleFileSelection && handleFileSelection(file?.file);
                                    file?.file && setSelectedFile({ file: file?.file, id: file?.id });
                                }}
                                selected={selectedFile}
                            />
                        );
                    }

                    return;
                })
            ) : (
                <p className={classes.succesMessage}>No images available for this folder</p>
            )}
            <WarningDialog
                open={showWarning}
                handleClose={handleCloseWarning}
                handleContinue={handleContinueWarning}
                title='Are you sure you want to delete this image?'
                content='By clicking continue the image will be deleted from the server and cannot be restored, this action will be permanent.'
            />
        </Grid>
    );
};

export default ImagesBlock;
